import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  Loader2, Search, MessageSquare, TrendingUp, 
  CheckCircle, Eye, Clock, ChevronRight, RefreshCw, BarChart3,
  Filter, Calendar, Mail, MessageCircle, AlertTriangle, Send, XCircle
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

export const AdminFeedback = () => {
  const navigate = useNavigate();
  const { adminRole } = useOutletContext();
  const isPrimaryAdmin = adminRole === 'primary';
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  
  // Notification status
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [testFeedbackLoading, setTestFeedbackLoading] = useState(false);
  const [testFeedbackResult, setTestFeedbackResult] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.q = searchQuery;
      if (roleFilter && roleFilter !== 'all') params.role = roleFilter;
      
      const [feedbackRes, statsRes, insightsRes, notifStatusRes] = await Promise.all([
        adminAPI.getFeedback(params),
        adminAPI.getFeedbackStats(),
        adminAPI.getFeedbackInsights(),
        adminAPI.getNotificationStatus(),
      ]);
      
      setFeedbacks(feedbackRes.data);
      setStats(statsRes.data);
      setInsights(insightsRes.data);
      setNotificationStatus(notifStatusRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery, roleFilter, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleSendTestFeedback = async () => {
    setTestFeedbackLoading(true);
    setTestFeedbackResult(null);
    try {
      const response = await adminAPI.sendTestFeedback();
      setTestFeedbackResult({ ...response.data, type: 'dry-run' });
    } catch (err) {
      setTestFeedbackResult({
        type: 'dry-run',
        db_save: { success: false, message: err.response?.data?.detail || 'Request failed' },
        email: { success: false, message: 'Could not test', enabled: false },
        discord: { success: false, message: 'Could not test', enabled: false }
      });
    } finally {
      setTestFeedbackLoading(false);
    }
  };
  
  const handleCreateTestFeedbackFull = async () => {
    setTestFeedbackLoading(true);
    setTestFeedbackResult(null);
    try {
      const response = await adminAPI.createTestFeedbackFull();
      setTestFeedbackResult({ ...response.data, type: 'full' });
      // Refresh data after creating real feedback
      fetchData();
    } catch (err) {
      setTestFeedbackResult({
        type: 'full',
        db_save: { success: false, message: err.response?.data?.detail || 'Request failed' },
        email: { success: false, message: 'Could not test', enabled: false },
        discord: { success: false, message: 'Could not test', enabled: false }
      });
    } finally {
      setTestFeedbackLoading(false);
    }
  };

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    try {
      await adminAPI.updateFeedbackStatus(feedbackId, newStatus);
      setFeedbacks(feedbacks.map(f => 
        f.id === feedbackId ? { ...f, status: newStatus } : f
      ));
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (err) {
      console.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      reviewed: 'bg-yellow-100 text-yellow-700',
      resolved: 'bg-green-100 text-green-700'
    };
    return (
      <Badge className={styles[status] || styles.new}>
        {status || 'new'}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    return role === 'owner' 
      ? <Badge className="bg-purple-100 text-purple-700">Owner</Badge>
      : <Badge className="bg-gray-100 text-gray-700">Tech</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold">{stats?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold">{stats?.by_status?.new || 0}</p>
                  <p className="text-xs text-muted-foreground">New</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold">{stats?.by_status?.resolved || 0}</p>
                  <p className="text-xs text-muted-foreground">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center shrink-0">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-bold">{stats?.last_7_days || 0}</p>
                  <p className="text-xs text-muted-foreground">Last 7d</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notification Status & Test Panel */}
        <Card className="mb-6 border-slate-200">
          <CardHeader className="pb-3 px-4 sm:px-6">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
              Developer Notification Status
            </CardTitle>
            {isPrimaryAdmin && <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSendTestFeedback}
                disabled={testFeedbackLoading}
                className="w-full sm:w-auto"
                data-testid="test-dry-run-btn"
              >
                {testFeedbackLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Test Dry Run
              </Button>
              <Button 
                variant="default" 
                size="sm" 
                onClick={handleCreateTestFeedbackFull}
                disabled={testFeedbackLoading}
                className="w-full sm:w-auto"
                data-testid="test-full-btn"
              >
                {testFeedbackLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                <span className="hidden sm:inline">Create Test Feedback (DB + Notify)</span>
                <span className="sm:hidden">Test Full (DB + Notify)</span>
              </Button>
            </div>}
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Email Status */}
              <div className={`p-4 rounded-lg border ${notificationStatus?.email?.enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${notificationStatus?.email?.enabled ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <Mail className={`w-5 h-5 ${notificationStatus?.email?.enabled ? 'text-green-600' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">Email Notifications</span>
                      {notificationStatus?.email?.enabled ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Configured</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Disabled</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {notificationStatus?.email?.message || (notificationStatus?.email?.enabled ? 'SMTP configured' : 'Email disabled (missing SMTP env vars)')}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Discord Status */}
              <div className={`p-4 rounded-lg border ${notificationStatus?.discord?.enabled ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${notificationStatus?.discord?.enabled ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <MessageCircle className={`w-5 h-5 ${notificationStatus?.discord?.enabled ? 'text-green-600' : 'text-amber-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">Discord Notifications</span>
                      {notificationStatus?.discord?.enabled ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Configured</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Disabled</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {notificationStatus?.discord?.message || (notificationStatus?.discord?.enabled ? 'Webhook configured' : 'Discord disabled (missing webhook URL)')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Test Feedback Results */}
            {testFeedbackResult && (
              <div className="mt-4 p-4 rounded-lg bg-muted border">
                <p className="font-medium text-sm mb-3">
                  Test Results {testFeedbackResult.type === 'full' ? '(Full - DB + Notifications)' : '(Dry Run - Notifications Only)'}:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    {testFeedbackResult.db_save?.success ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600" />
                    )}
                    <span className={testFeedbackResult.db_save?.success ? 'text-green-700' : 'text-red-700'}>
                      DB {testFeedbackResult.type === 'full' ? 'Save' : 'Check'}: {testFeedbackResult.db_save?.message}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {testFeedbackResult.email?.success ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : testFeedbackResult.email?.enabled ? (
                      <XCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className={
                      testFeedbackResult.email?.success ? 'text-green-700' : 
                      testFeedbackResult.email?.enabled ? 'text-red-700' : 'text-amber-700'
                    }>
                      Email: {testFeedbackResult.email?.message}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    {testFeedbackResult.discord?.success ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : testFeedbackResult.discord?.enabled ? (
                      <XCircle className="w-4 h-4 text-red-600" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className={
                      testFeedbackResult.discord?.success ? 'text-green-700' : 
                      testFeedbackResult.discord?.enabled ? 'text-red-700' : 'text-amber-700'
                    }>
                      Discord: {testFeedbackResult.discord?.message}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Insights Summary */}
        {insights?.summary && (
          <Card className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                Insights Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-foreground">{insights.summary}</p>
              {insights.top_keywords?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {insights.top_keywords.slice(0, 8).map((kw, idx) => (
                    <Badge key={idx} variant="outline" className="bg-card">
                      {kw.word} ({kw.count})
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Feedback List */}
          <div className="lg:col-span-2 min-w-0">
            <Card>
              <CardHeader className="px-4 sm:px-6">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <MessageSquare className="w-5 h-5 shrink-0" />
                    Feedback ({feedbacks.length})
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchData} className="shrink-0">
                    <RefreshCw className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Refresh</span>
                  </Button>
                </div>
                
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search feedback..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full sm:w-[130px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="reviewed">Reviewed</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                      <SelectTrigger className="w-full sm:w-[110px]">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="tech">Tech</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                {feedbacks.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No feedback found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {feedbacks.map((fb) => (
                      <button
                        key={fb.id}
                        onClick={() => setSelectedFeedback(fb)}
                        className="w-full text-left p-4 rounded-lg border bg-card hover:bg-muted hover:border-primary/50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm truncate">{fb.tenant_name}</span>
                              {getRoleBadge(fb.user_role)}
                              {getStatusBadge(fb.status)}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {fb.user_name} · {fb.context_json?.route || 'Unknown page'}
                            </p>
                            <p className="text-sm line-clamp-2">{fb.message}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDistanceToNow(new Date(fb.created_at), { addSuffix: true })}
                            </span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Tenants & Clusters */}
          <div className="space-y-6">
            {/* Top Tenants */}
            {stats?.by_tenant?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Tenants</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.by_tenant.slice(0, 5).map((t, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-sm truncate">{t.tenant}</span>
                        <Badge variant="secondary">{t.count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Topic Clusters */}
            {insights?.clusters?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Topic Clusters</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {insights.clusters.map((cluster, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm capitalize">{cluster.title}</span>
                          <Badge variant="outline">{cluster.count}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {cluster.examples?.[0]}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Feature Requests */}
            {insights?.feature_requests?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Feature Requests ({insights.feature_requests.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.feature_requests.slice(0, 5).map((fr, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="text-sm line-clamp-2">{fr.message}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Detail Modal */}
      <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Feedback Details</DialogTitle>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                {getStatusBadge(selectedFeedback.status)}
                {getRoleBadge(selectedFeedback.user_role)}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Tenant</p>
                  <p className="font-medium">{selectedFeedback.tenant_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">User</p>
                  <p className="font-medium">{selectedFeedback.user_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedFeedback.user_email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Page</p>
                  <p className="font-medium font-mono text-xs">{selectedFeedback.context_json?.route || 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{format(new Date(selectedFeedback.created_at), 'PPpp')}</p>
                </div>
              </div>
              
              <div>
                <p className="text-muted-foreground text-sm mb-2">Message</p>
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedFeedback.message}</p>
                </div>
              </div>
              
              {selectedFeedback.contact_email && (
                <div>
                  <p className="text-muted-foreground text-sm">Contact Email</p>
                  <p className="font-medium">{selectedFeedback.contact_email}</p>
                </div>
              )}
              
              {selectedFeedback.context_json && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground">Context JSON</summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                    {JSON.stringify(selectedFeedback.context_json, null, 2)}
                  </pre>
                </details>
              )}
              
              {isPrimaryAdmin && <div className="flex gap-2 pt-4 border-t">
                {selectedFeedback.status !== 'reviewed' && (
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'reviewed')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Mark Reviewed
                  </Button>
                )}
                {selectedFeedback.status !== 'resolved' && (
                  <Button 
                    className="flex-1"
                    onClick={() => handleStatusUpdate(selectedFeedback.id, 'resolved')}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Resolved
                  </Button>
                )}
              </div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
